import {
  CreatePharmacyProductDto,
  GetManyPharmacyProductByQuantity,
  GetPharmacyProductCountByQuantity,
  UnpackPharmacyProduct,
  UpdatePharmacyProductTaxRateDto,
} from "../client";
import {
  GetManyPharmacyProductsQueryParams,
  GetManyQueryParams,
  PharmacyProduct,
  PharmacyProductPage,
  PharmacyProductStats,
  PharmacyProductSummary,
  PharmacyProductSummaryPage,
  UpdatePharmacyProduct,
  UpdatePharmacyProductBatchEdit,
} from "../dtos";
import { StockHistory } from "../dtos/StockHistory";
import { BaseEndpoint } from "./BaseEndpoint";
import { WithEdit } from "./mixins/WithEdit";
import { WithGet } from "./mixins/WithGet";
import { PharmacyProductStorageEndpoint } from "./PharmacyProductStorageEndpoint";

export interface AssociateProduct {
  productId: string;
}

export interface UpdatePharmacyProductCategory {
  productCategoryId: string;
}

export class Endpoint extends BaseEndpoint("pharmacy-products") {
  readonly storages = new PharmacyProductStorageEndpoint(this.client);

  getPage(query?: GetManyPharmacyProductsQueryParams): Promise<PharmacyProductSummaryPage> {
    return this.request("", { query });
  }

  getPageByIds(
    query: GetManyQueryParams,
    body: Pick<GetManyPharmacyProductsQueryParams, "ids">,
  ): Promise<PharmacyProductSummaryPage> {
    return this.request("search/by-ids", { method: "POST", query, body });
  }

  getStats(query?: GetManyPharmacyProductsQueryParams): Promise<PharmacyProductStats> {
    return this.request("stats", { query });
  }

  associateProduct(id: string, body: AssociateProduct): Promise<PharmacyProduct> {
    return this.request(`${id}/associate-product`, { method: "POST", body });
  }

  unpack(body: UnpackPharmacyProduct): Promise<PharmacyProduct[]> {
    return this.request("unpack", { method: "PATCH", body });
  }

  getAll(query: Omit<GetManyQueryParams, "size">): Promise<PharmacyProductPage> {
    return this.request("all", { query });
  }

  getCountByStockQuantity({ min, max }: GetManyPharmacyProductByQuantity): Promise<GetPharmacyProductCountByQuantity> {
    return this.request("count-by-stock-quantity", { query: { min, max } });
  }

  getByStockItemId(id: string): Promise<PharmacyProductSummary> {
    return this.request(`stockItem/${id}`);
  }

  getArchivedProducts(pharmacyProductIds: string[]): Promise<PharmacyProduct[]> {
    return this.request("archivedProducts", { method: "POST", body: { pharmacyProductIds } });
  }

  batchUpdate(body: UpdatePharmacyProductBatchEdit): Promise<void> {
    return this.request("batch-update", { method: "POST", body });
  }

  updateTaxRate(id: string, body: UpdatePharmacyProductTaxRateDto): Promise<PharmacyProduct> {
    return this.request(`${id}/tax-rate-update`, { method: "POST", body });
  }
}

const EditEndoint = WithEdit<typeof Endpoint, PharmacyProduct, CreatePharmacyProductDto, UpdatePharmacyProduct>(
  Endpoint,
);

export const PharmacyProductEndpoint = WithGet<typeof EditEndoint, PharmacyProduct>(EditEndoint);
